import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, LogOut, Users, Pencil, KeyRound, Plus, Image as ImageIcon } from "lucide-react";
import { getToken, clearToken, authFetch } from "@/adminAuth";

const API = `/api`;

const chip = (active) =>
  `px-4 py-2 text-[11px] uppercase tracking-[0.15em] border transition-colors duration-300 ${
    active
      ? "bg-gold text-ink border-gold font-medium"
      : "border-white/15 text-[#A0A5B1] hover:border-gold/60 hover:text-gold"
  }`;

export default function AdminFiches() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [reviewOnly, setReviewOnly] = useState(false);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [vocabTypes, setVocabTypes] = useState([]);
  const [newType, setNewType] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [createError, setCreateError] = useState("");
  const [createBusy, setCreateBusy] = useState(false);

  useEffect(() => {
    if (!getToken()) navigate("/admin/login");
    if (new URLSearchParams(window.location.search).get("nouvelle") === "1") setCreating(true);
  }, [navigate]);

  useEffect(() => {
    authFetch(`${API}/admin/vocab`)
      .then((r) => r.json())
      .then((d) => setVocabTypes(d.types || []))
      .catch(() => {});
  }, []);

  const createFiche = async () => {
    if (!newType || !newTitle.trim()) {
      setCreateError("Choisissez un type et saisissez un titre.");
      return;
    }
    setCreateBusy(true);
    setCreateError("");
    try {
      const resp = await authFetch(`${API}/admin/knowledge`, {
        method: "POST",
        body: JSON.stringify({ type: newType, title: newTitle.trim() }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.slug) {
        navigate(`/admin/fiches/${data.type}/${data.slug}`);
      } else {
        setCreateError(typeof data.detail === "string" ? data.detail : "La création a échoué — réessayez.");
      }
    } catch {
      setCreateError("La création a échoué — réessayez.");
    }
    setCreateBusy(false);
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    const t = setTimeout(() => {
      authFetch(`${API}/admin/knowledge?${params}`)
        .then((r) => r.json())
        .then(setItems)
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [type, status, q]);

  const types = items ? [...new Set(items.map((i) => i.type))] : [];
  const labelOf = (t) => items.find((i) => i.type === t)?.type_label || t;

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="admin-fiches-page">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="label-accent">Back-office</span>
            <h1 className="mt-4 font-serif font-light text-3xl sm:text-4xl">Les fiches</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCreating(!creating)}
              data-testid="admin-new-fiche-button"
              className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.2em] font-medium px-4 py-2.5 hover:bg-gold-light transition-colors"
            >
              <Plus className="w-4 h-4" /> Nouvelle fiche
            </button>
            <Link
              to="/admin/equipe"
              data-testid="admin-team-link"
              className="inline-flex items-center gap-2 border border-white/15 text-[#A0A5B1] text-xs uppercase tracking-[0.2em] px-4 py-2.5 hover:border-gold/60 hover:text-gold transition-colors"
            >
              <Users className="w-4 h-4" /> Équipe
            </Link>
            <Link
              to="/admin/compte"
              data-testid="admin-account-link"
              className="inline-flex items-center gap-2 border border-white/15 text-[#A0A5B1] text-xs uppercase tracking-[0.2em] px-4 py-2.5 hover:border-gold/60 hover:text-gold transition-colors"
            >
              <KeyRound className="w-4 h-4" /> Mot de passe
            </Link>
            <Link
              to="/admin/medias"
              data-testid="admin-media-link"
              className="inline-flex items-center gap-2 border border-white/15 text-[#A0A5B1] text-xs uppercase tracking-[0.2em] px-4 py-2.5 hover:border-gold/60 hover:text-gold transition-colors"
            >
              <ImageIcon className="w-4 h-4" /> Médiathèque
            </Link>
            <a
              href="/api/archive"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-archive-link"
              className="inline-flex items-center gap-2 border border-white/15 text-[#A0A5B1] text-xs uppercase tracking-[0.2em] px-4 py-2.5 hover:border-gold/60 hover:text-gold transition-colors"
            >
              Archive ancien site ↗
            </a>
            <a
              href="/orientation"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-wizard-preview-link"
              className="inline-flex items-center gap-2 border border-white/15 text-[#A0A5B1] text-xs uppercase tracking-[0.2em] px-4 py-2.5 hover:border-gold/60 hover:text-gold transition-colors"
            >
              Tester le wizard ↗
            </a>
            <button
              onClick={() => { clearToken(); navigate("/admin/login"); }}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-white/15 text-[#A0A5B1] text-xs uppercase tracking-[0.2em] px-4 py-2.5 hover:border-red-400 hover:text-red-400 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          {creating && (
            <div className="w-full mb-4 border border-gold/25 bg-ink-card p-6 flex flex-wrap items-end gap-4" data-testid="admin-create-panel">
              <div className="min-w-56">
                <label className="label-accent block mb-2">1. Type de fiche</label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  className="w-full bg-ink border border-white/15 px-4 py-2.5 text-sm text-[#FAF8F5] outline-none focus:border-gold cursor-pointer"
                  data-testid="admin-create-type"
                >
                  <option value="" className="bg-ink">Choisir le type…</option>
                  {vocabTypes.map((t) => (
                    <option key={t.key} value={t.key} className="bg-ink">{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-64">
                <label className="label-accent block mb-2">2. Titre de la fiche</label>
                <input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Ex. Clause de non-sollicitation"
                  className="w-full bg-transparent border border-white/15 px-4 py-2.5 text-sm text-[#FAF8F5] outline-none focus:border-gold placeholder:text-[#626773]"
                  data-testid="admin-create-title"
                  onKeyDown={(e) => e.key === "Enter" && createFiche()}
                />
              </div>
              <button
                onClick={createFiche}
                disabled={createBusy}
                data-testid="admin-create-submit"
                className="bg-gold text-ink text-xs uppercase tracking-[0.2em] font-medium px-6 py-3 hover:bg-gold-light transition-colors disabled:opacity-50"
              >
                {createBusy ? "Création…" : "Créer (brouillon)"}
              </button>
              {createError && (
                <p className="w-full text-xs text-red-400" data-testid="admin-create-error">{createError}</p>
              )}
              <p className="w-full text-xs text-[#626773]">
                La fiche est créée en brouillon (non publiée) : vous la complétez, puis vous la publiez depuis sa page d'édition.
              </p>
            </div>
          )}
          <div className="flex flex-1 min-w-64 max-w-md border border-white/15 focus-within:border-gold transition-colors">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher une fiche…"
              data-testid="admin-search-input"
              className="flex-1 bg-transparent outline-none px-4 py-2.5 text-sm text-[#FAF8F5] placeholder:text-[#626773]"
            />
            <span className="px-4 flex items-center text-[#626773]"><Search className="w-4 h-4" /></span>
          </div>
          <button onClick={() => setType("")} className={chip(type === "")} data-testid="admin-filter-all">
            Tous types
          </button>
          {types.map((t) => (
            <button key={t} onClick={() => setType(t)} className={chip(type === t)} data-testid={`admin-filter-${t}`}>
              {labelOf(t)}
            </button>
          ))}
        </div>

        <div className="mt-4 flex gap-3 flex-wrap">
          <button onClick={() => setStatus("")} className={chip(status === "")} data-testid="admin-status-all">Tous statuts</button>
          <button onClick={() => setStatus("publie")} className={chip(status === "publie")} data-testid="admin-status-publie">Publiées</button>
          <button onClick={() => setStatus("archive")} className={chip(status === "archive")} data-testid="admin-status-archive">Archivées</button>
          <button
            onClick={() => { const next = !reviewOnly; setReviewOnly(next); if (next) setStatus(""); }}
            className={chip(reviewOnly)}
            data-testid="admin-status-review"
          >
            Brouillons à valider ({items ? items.filter((i) => i.needs_review).length : 0})
          </button>
        </div>

        {!items ? (
          <p className="mt-16 text-[#626773] font-mono text-sm">Chargement…</p>
        ) : (
          <div className="mt-8 border border-white/5" data-testid="admin-fiches-table">
            <div className="px-6 py-3 border-b border-white/5 flex justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[#626773]">
              <span>{(reviewOnly ? items.filter((i) => i.needs_review) : items).length} fiche{(reviewOnly ? items.filter((i) => i.needs_review) : items).length > 1 ? "s" : ""}{reviewOnly ? " à valider" : ""}</span>
              <span>Statut</span>
            </div>
            {(reviewOnly ? items.filter((i) => i.needs_review) : items).map((i) => (
              <Link
                key={`${i.type}-${i.slug}`}
                to={`/admin/fiches/${i.type}/${i.slug}`}
                data-testid={`admin-row-${i.slug}`}
                className="group px-6 py-4 border-b border-white/5 flex items-center justify-between gap-6 hover:bg-ink-card transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-serif text-lg truncate group-hover:text-gold-light transition-colors">{i.title}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#626773]">
                    {i.type_label} · {i.slug}
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  {i.needs_review && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border border-gold/50 text-gold bg-gold/10" data-testid={`admin-badge-review-${i.slug}`}>
                      À valider
                    </span>
                  )}
                  <span
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border ${
                      i.status === "publie" ? "text-gold border-gold/30" : "text-[#626773] border-white/10"
                    }`}
                  >
                    {i.status === "publie" ? "Publiée" : "Archivée"}
                  </span>
                  <Pencil className="w-4 h-4 text-[#626773] group-hover:text-gold transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
